/**
 * 登录页面组件
 * Login Page Component
 * Feature: mobile-responsive-ui-adaptation
 */
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Tabs, Typography, Space } from 'antd';
import { UserOutlined, LockOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import { MobileForm } from '../../responsive/components/MobileForm';
import { useTheme } from '../../providers/ThemeAdaptorProvider';
import LoginGlassPage from '../LoginGlass';
import './index.css';

const { Title, Text } = Typography;

interface LoginFormData {
  username: string;
  password: string;
}

interface RegisterFormData {
  username: string;
  password: string;
  confirmPassword: string;
  realName: string;
  phone: string;
  email?: string;
}

const LoginPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [loginForm] = Form.useForm<LoginFormData>();
  const [registerForm] = Form.useForm<RegisterFormData>();
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, loading, error, clearError, isAuthenticated } = useAuthStore();
  const { isMobile, isTablet } = useResponsiveConfig();
  const { currentTheme } = useTheme();

  // 如果已经登录，重定向到首页
  useEffect(() => {
    const isPreview = new URLSearchParams(location.search).has('preview');
    if (isAuthenticated && !isPreview) {
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  // 清除错误信息
  useEffect(() => {
    if (error) {
      message.error(error);
      clearError();
    }
  }, [error, clearError]);

  // 如果主题是 glass，渲染独立的玻璃态组件
  if (currentTheme === 'glass') {
    return <LoginGlassPage />;
  }

  // 处理登录
  const handleLogin = async (values: LoginFormData) => {
    const result = await login(values);
    
    if (result.success) {
      message.success(result.message);
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } else {
      message.error(result.message);
    }
  };

  // 处理注册
  const handleRegister = async (values: RegisterFormData) => {
    // 手机号作为用户名
    const registerData = {
      username: values.phone,  // 使用手机号作为用户名
      phone: values.phone,
      realName: values.realName,
      password: values.password,
      confirmPassword: values.confirmPassword,
      email: values.email
    };
    
    const result = await register(registerData);
    
    if (result.success) {
      message.success(result.message);
      const from = (location.state as any)?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } else {
      message.error(result.message);
    }
  };

  const tabItems = [
    {
      key: 'login',
      label: '登录',
      children: (
        <MobileForm
          form={loginForm}
          name="login"
          onFinish={handleLogin}
          autoScrollToError
          keyboardAware
          submitButton={{
            text: '登录',
            loading: loading,
            fullWidth: true
          }}
          className="login-form"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' },
              { max: 30, message: '用户名不能超过30个字符' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: '用户名只能包含字母、数字和下划线' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名"
              autoComplete="username"
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              autoComplete="current-password"
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>

          <div className="login-tips">
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
              仅斌宇林建内部使用
            </Text>
          </div>
        </MobileForm>
      ),
    },
    {
      key: 'register',
      label: '注册',
      children: (
        <MobileForm
          form={registerForm}
          name="register"
          onFinish={handleRegister}
          autoScrollToError
          keyboardAware
          submitButton={{
            text: '注册',
            loading: loading,
            fullWidth: true
          }}
          className="register-form"
        >
          <Form.Item
            name="realName"
            rules={[
              { required: true, message: '请输入真实姓名' },
              { max: 50, message: '姓名不能超过50个字符' }
            ]}
          >
            <Input
              placeholder="真实姓名"
              autoComplete="name"
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>

          <Form.Item
            name="phone"
            rules={[
              { required: true, message: '请输入手机号码' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' }
            ]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="手机号码（将作为登录账号）"
              autoComplete="tel"
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="邮箱地址（可选）"
              autoComplete="email"
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6个字符' },
              { max: 128, message: '密码不能超过128个字符' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码（至少6位）"
              autoComplete="new-password"
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="确认密码"
              autoComplete="new-password"
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>

          <div className="register-tips">
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 13 }}>
              注册后将获得基础查看权限，如需更多权限请联系管理员
            </Text>
          </div>
        </MobileForm>
      ),
    },
  ];

  return (
    <div className="login-container">
      {currentTheme === 'glass' && (
        <>
          <div className="glass-orb one"></div>
          <div className="glass-orb two"></div>
        </>
      )}
      <div className="login-background">
        <div className="login-content">
          <Card 
            className="login-card" 
            variant="borderless"
            styles={{ body: { padding: '20px 24px 22px' } }}
          >
            <div className="login-header">
              <Title level={isMobile ? 3 : 2} className="login-title">
                {currentTheme === 'glass' ? '欢迎回来' : '斌宇林建项目管理系统'}
              </Title>
              <Text type="secondary" style={{ fontSize: isMobile ? 13 : 14 }}>
                斌宇林建项目管理系统
              </Text>
            </div>

            <Tabs 
              activeKey={activeTab} 
              onChange={setActiveTab}
              centered
              className="login-tabs"
              size={isMobile ? 'large' : 'middle'}
              items={tabItems}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
